// ===========================================
// Waive Registration Fee Dialog
// ===========================================
// Waives a family's registration fee (a reason is required, and is kept in
// the audit row). md/plan/family-payment-history.md Task 4.3.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useWaiveRegistrationFee } from '../../../hooks/useFamilyPayments';
import { apiErrorMessage } from '../../../utils/apiError';

interface WaiveRegistrationFeeDialogProps {
  open: boolean;
  onClose: () => void;
  familyId: string | undefined;
}

export default function WaiveRegistrationFeeDialog({ open, onClose, familyId }: WaiveRegistrationFeeDialogProps) {
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutation = useWaiveRegistrationFee();

  useEffect(() => {
    if (!open) return;
    setReason('');
    setSubmitError(null);
  }, [open]);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const trimmed = reason.trim();
  const valid = trimmed.length > 0;

  const handleSubmit = async () => {
    if (!familyId || !valid) return;
    setSubmitError(null);
    try {
      await mutation.mutateAsync({ familyId, reason: trimmed });
      onClose();
    } catch (error) {
      setSubmitError(apiErrorMessage(error, 'Failed to waive the registration fee'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Waive Registration Fee</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            The family will not be asked for the registration fee. You can revoke this later.
          </Typography>
          <TextField
            fullWidth
            required
            autoFocus
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            disabled={mutation.isPending}
            error={reason !== '' && !valid}
            helperText={reason !== '' && !valid ? 'A reason is required' : undefined}
            inputProps={{ maxLength: 500 }}
          />
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mutation.isPending ? 'Waiving...' : 'Waive Fee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
